import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import CONFIG from '@playcastdotio/GlobalConfig';
import { useActiveCampaigns } from '../../../hooks/useActiveCampaigns';
import { sendAnalytics } from '@playcastdotio/Analytics';
import { getPersistentPlayerId } from '../../../api';
import { authStore } from '../../../api/stores/globalStores';
import '../../../styles/aida-base.css';
import '../../../styles/aida-responsive.css';

/**
 * RWBYCarousel Component
 * Limited-time event version of CampaignCarousel. 1:1 copy — swap this out when the event ends.
 * Displays active campaigns in a 3D carousel with autoplay, drag, and keyboard navigation.
 * Header: "PLAY ALL GAMES INSTANTLY. ANY PLACE, ANY TIME"
 * Desktop: Absolute-positioned layout at 1024px design width
 * Mobile: Scaled-down version of the same carousel/card with a single Play Now button
 */

type RWBYCarouselSlide = {
  id: string;
  title: string;
  description: string;
  demoUrl: string;
  videoSrc?: string;
  posterSrc?: string;
};

const SECTION_HEIGHT = 420;

const AUTOPLAY_MS = 6500;
const IDLE_RESUME_MS = 9000;
const FALLBACK_POSTER = 'https://i.imgur.com/LwEz0bc.jpg';
const DECORATIVE_IMG = 'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/4610144/image-0bdd0551-e329-4b7b-baf5-bbc90abfb745.webp';
const WHITE = 'rgba(255, 255, 255, 1)';

const modIndex = (value: number, modBy: number) => (value % modBy + modBy) % modBy;

const normalizeTheme = (theme: Record<string, any>) => {
  const nestedTheme = theme?.theme;
  if (nestedTheme && typeof nestedTheme === 'object') {
    const nestedRecord = nestedTheme as Record<string, any>;
    if (nestedRecord.theme && typeof nestedRecord.theme === 'object') {
      return nestedRecord.theme as Record<string, any>;
    }
    return nestedTheme as Record<string, any>;
  }
  return theme;
};

const isVideoUrl = (value?: string) => Boolean(value && /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(value));

const resolveThemeVideo = (themeInput: Record<string, any>) => {
  const theme = normalizeTheme(themeInput);
  const trailer = theme?.trailerBackgroundVideo;
  const loading = theme?.loadingBackgroundVideo;
  const trailerVideoUrl =
    typeof trailer === 'string' ? trailer : typeof trailer?.videoUrl === 'string' ? trailer.videoUrl : undefined;
  const loadingVideoUrl =
    typeof loading === 'string' ? loading : typeof loading?.videoUrl === 'string' ? loading.videoUrl : undefined;
  const trailerPoster =
    typeof trailer === 'object' && typeof trailer?.posterImage === 'string' ? trailer.posterImage : undefined;
  const loadingPoster =
    typeof loading === 'object' && typeof loading?.posterImage === 'string' ? loading.posterImage : undefined;
  const fallbackVideo =
    (isVideoUrl(theme?.playNowModalBackgroundImage) ? theme?.playNowModalBackgroundImage : undefined) ??
    (isVideoUrl(theme?.pageBackgroundImage) ? theme?.pageBackgroundImage : undefined);
  return {
    videoUrl: trailerVideoUrl ?? loadingVideoUrl ?? theme?.playNowModalBackgroundVideo ?? theme?.videoUrl ?? theme?.trailerVideoUrl ?? fallbackVideo,
    posterUrl:
      trailerPoster ??
      loadingPoster ??
      theme?.playNowModalBackgroundImage ??
      theme?.pageBackgroundImage ??
      theme?.wishlistEmailGameImageUrl ??
      theme?.gameLogo ??
      FALLBACK_POSTER,
  };
};

const CAROUSEL_STYLES = `
  .rm-campaign-carousel {
    width: 360px;
    margin: 0 auto;
    perspective: 1200px;
    font-family: var(--font-inter, 'Inter', sans-serif);
    height: 260px;
    transform: scale(1.1);
    transform-origin: center;
  }

  .rm-campaign-carousel-track {
    position: relative;
    height: 100%;
    overflow: visible;
  }

  .rm-campaign-slide {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 240px;
    transform-style: preserve-3d;
    transition: transform 0.55s cubic-bezier(.22,.61,.36,1), opacity 0.4s ease;
    cursor: pointer;
    z-index: 1;
  }

  .rm-campaign-slide.center {
    transform: translate(-50%, -50%) scale(1);
    z-index: 3;
  }

  .rm-campaign-slide.center:hover {
    transform: translate(-50%, -50%) scale(1.07);
  }

  .rm-campaign-slide.left {
    transform: translate(calc(-50% - 120px), -50%) scale(0.85);
    z-index: 2;
  }

  .rm-campaign-slide.right {
    transform: translate(calc(-50% + 120px), -50%) scale(0.85);
    z-index: 2;
  }

  .rm-campaign-slide.far-left {
    opacity: 0;
    pointer-events: none;
    transform: translate(calc(-50% - 240px), -50%) scale(0.75);
    z-index: 0;
  }

  .rm-campaign-slide.far-right {
    opacity: 0;
    pointer-events: none;
    transform: translate(calc(-50% + 240px), -50%) scale(0.75);
    z-index: 0;
  }

  .rm-campaign-card-border {
    position: relative;
    height: 150px;
    border-radius: var(--radius-xl, 20px);
    padding: 1px;
    background: linear-gradient(135deg, rgba(190,120,255,1), rgba(255,205,110,1), rgba(190,120,255,1));
    background-size: 400% 400%;
    background-position: 0% 50%;
    box-shadow:
      0 0 20px rgba(190,120,255,0.5),
      0 0 40px rgba(114,137,218,0.25);
  }

  .rm-campaign-card {
    width: 100%;
    height: 100%;
    border-radius: calc(var(--radius-xl, 20px) - 1px);
    overflow: hidden;
    background: var(--color-black, #000);
  }

  .rm-campaign-card video,
  .rm-campaign-card img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    pointer-events: none;
    display: block;
  }

  .rm-campaign-slide.left video,
  .rm-campaign-slide.right video,
  .rm-campaign-slide.left img,
  .rm-campaign-slide.right img {
    filter: brightness(0.5);
  }

  .rm-campaign-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: flex-end;
    padding: 10px 14px 12px;
    z-index: 6;
    color: var(--color-white, #fff);
    pointer-events: none;
  }

  .rm-campaign-ui {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    width: 100%;
    gap: 10px;
  }

  .rm-campaign-title {
    font-size: 10px;
    font-weight: 600;
    line-height: 1.15;
  }

  .rm-campaign-desc {
    font-size: 6.8px;
    opacity: 0.7;
    line-height: 1.3;
  }

  .rm-campaign-dots {
    position: absolute;
    bottom: 40px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 6px;
    z-index: 10;
  }

  .rm-campaign-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--color-white-25, rgba(255,255,255,.3));
    cursor: pointer;
  }

  .rm-campaign-dot.active {
    background: var(--color-white, #fff);
  }

  .rm-campaign-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 22px;
    cursor: pointer;
    z-index: 20;
    user-select: none;
    -webkit-user-select: none;
  }

  .rm-campaign-arrow-left { left: -96px; }
  .rm-campaign-arrow-right { right: -96px; }

  .rm-campaign-arrow svg {
    width: 100%;
    height: 100%;
    display: block;
  }

  .rm-campaign-arrow:hover { opacity: 0.7; }

  @keyframes rm-campaign-ring-rotate {
    0%   { background-position: 0% 50%; }
    25%  { background-position: 100% 50%; }
    50%  { background-position: 100% 0%; }
    75%  { background-position: 0% 0%; }
    100% { background-position: 0% 50%; }
  }

  .rm-campaign-slide:hover .rm-campaign-card-border {
    animation: rm-campaign-ring-rotate 1.4s ease-in-out 1;
  }

`;


interface RWBYCarouselProps {
  onActiveDemoUrl?: (url: string | null) => void;
}

export const RWBYCarousel = ({ onActiveDemoUrl }: RWBYCarouselProps) => {
  const sectionRef = useRef(null);
  const mobileRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });
  const isMobileInView = useInView(mobileRef, { once: true, amount: 0.1 });

  const { campaigns, loading: campaignsLoading } = useActiveCampaigns();



  const slides = useMemo<RWBYCarouselSlide[]>(() => {
    if (!campaigns.length) return [];

    return campaigns
      .filter((theme) => {
        const normalized = normalizeTheme(theme);
        return normalized.isPublic === true || theme.isPublic === true;
      })
      .map((theme): RWBYCarouselSlide | null => {
        const normalizedTheme = normalizeTheme(theme);
        const slug =
          normalizedTheme.publicSlug ??
          normalizedTheme.urlSlug ??
          normalizedTheme.urlSlugs?.[0] ??
          normalizedTheme.slug ??
          normalizedTheme.campaignId ??
          theme.publicSlug ??
          theme.urlSlug ??
          theme.urlSlugs?.[0] ??
          theme.slug ??
          theme.campaignId;
        if (!slug) return null;

        const { videoUrl, posterUrl } = resolveThemeVideo(normalizedTheme);
        return {
          id: slug,
          title: normalizedTheme.gameName ?? normalizedTheme.displayName ?? slug,
          description:
            normalizedTheme.tagline ??
            normalizedTheme.shortDescription ??
            normalizedTheme.description ??
            (normalizedTheme.studioName ? `By ${normalizedTheme.studioName}` : 'Playcast campaign'),
          demoUrl: `${CONFIG.DEMO_BASE_URL}q/${slug}?utm_source=playcast-landing`,
          videoSrc: videoUrl,
          posterSrc: posterUrl ?? FALLBACK_POSTER,
        };
      })
      .filter((slide): slide is RWBYCarouselSlide => Boolean(slide));
  }, [campaigns]);

  const hasSlides = slides.length > 0;
  const slideCount = slides.length;

  const carouselSlides = useMemo(() => {
    if (slideCount <= 1) return slides;
    if (slideCount === 2) {
      return [slides[0], slides[1], slides[0], slides[1], slides[0], slides[1]];
    }
    if (slideCount === 3) return slides;
    if (slideCount % 2 === 0) {
      return [...slides, slides[0], slides[1], slides[2]];
    }
    return slides;
  }, [slides, slideCount]);

  const total = carouselSlides.length || 1;
  const [centerIndex, setCenterIndex] = useState(0);
  const lastInteractionRef = useRef(Date.now());
  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasSlides) return;
    setCenterIndex(Math.floor(total / 2));
  }, [hasSlides, total]);

  useEffect(() => {
    const url = carouselSlides.length > 0 ? carouselSlides[centerIndex]?.demoUrl ?? null : null;
    onActiveDemoUrl?.(url);
  }, [centerIndex, carouselSlides, onActiveDemoUrl]);

  const markInteraction = () => { lastInteractionRef.current = Date.now(); };
  const update = (index: number) => { setCenterIndex(modIndex(index, total)); };




  const handleKeyActivate = (event: React.KeyboardEvent<HTMLElement>, action: () => void) => {
    if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); action(); }
  };

  useEffect(() => {
    if (!hasSlides) return;
    const root = carouselRef.current;
    if (!root) return;
    const handler = () => markInteraction();
    const events = ['mouseenter', 'mousemove', 'pointerdown', 'touchstart', 'keydown', 'focusin', 'click'];
    events.forEach((evt) => root.addEventListener(evt, handler, { passive: true }));
    return () => { events.forEach((evt) => root.removeEventListener(evt, handler)); };
  }, [hasSlides]);

  useEffect(() => {
    if (slideCount < 2) return;
    const interval = window.setInterval(() => {
      if (Date.now() - lastInteractionRef.current >= IDLE_RESUME_MS) {
        setCenterIndex((current) => modIndex(current + 1, total));
      }
    }, AUTOPLAY_MS);
    return () => window.clearInterval(interval);
  }, [slideCount, total]);

  const leftIndex = modIndex(centerIndex - 1, total);
  const rightIndex = modIndex(centerIndex + 1, total);

  const openInNewTab = (url: string) => window.open(url, '_blank');

  const currentSlide = carouselSlides[centerIndex] ?? null;

  const handlePlayNow = () => {
    if (!currentSlide) return;
    sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: 'Play Now!', buttonLocation: 'RWBYCarousel' });
    openInNewTab(currentSlide.demoUrl);
  };

  const getSlideClass = (index: number) => {
    if (index === centerIndex) return 'center';
    if (index === leftIndex) return 'left';
    if (index === rightIndex) return 'right';
    const distRight = modIndex(index - centerIndex, total);
    const distLeft = modIndex(centerIndex - index, total);
    return distLeft <= distRight ? 'far-left' : 'far-right';
  };

  const renderCarousel = () => {
    if (slideCount < 2) return null;

    return (
      <div ref={carouselRef} className="rm-campaign-carousel">
        <div className="rm-campaign-carousel-track">
          <div
            className="rm-campaign-arrow rm-campaign-arrow-left"
            role="button"
            tabIndex={0}
            aria-label="Previous"
            onClick={() => { sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: 'Carousel Previous', buttonLocation: 'RWBYCarousel' }); update(centerIndex - 1); markInteraction(); }}
            onKeyDown={(e) => handleKeyActivate(e, () => { update(centerIndex - 1); markInteraction(); })}
          >
            <svg viewBox="0 0 34 44" xmlns="http://www.w3.org/2000/svg">
              <path d="M2 22 L32 2 L26 22 L32 42 Z" fill="#fff" />
            </svg>
          </div>

          <div
            className="rm-campaign-arrow rm-campaign-arrow-right"
            role="button"
            tabIndex={0}
            aria-label="Next"
            onClick={() => { sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: 'Carousel Next', buttonLocation: 'RWBYCarousel' }); update(centerIndex + 1); markInteraction(); }}
            onKeyDown={(e) => handleKeyActivate(e, () => { update(centerIndex + 1); markInteraction(); })}
          >
            <svg viewBox="0 0 34 44" xmlns="http://www.w3.org/2000/svg">
              <path d="M32 22 L2 2 L8 22 L2 42 Z" fill="#fff" />
            </svg>
          </div>

          {carouselSlides.map((slide, index) => (
            <div
              key={`${slide.id}-${index}`}
              className={`rm-campaign-slide ${getSlideClass(index)}`}
              data-index={index}
              role="button"
              tabIndex={0}
              onClick={() => {
                if (index === centerIndex) {
                  sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: `Play Now: ${slide.title}`, buttonLocation: 'RWBYCarousel' });
                  openInNewTab(slide.demoUrl);
                  return;
                }
                sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: `Carousel Slide: ${slide.title}`, buttonLocation: 'RWBYCarousel' });
                update(index);
                markInteraction();
              }}
              onKeyDown={(e) => handleKeyActivate(e, () => { update(index); markInteraction(); })}
            >
              <div className="rm-campaign-card-border">
                <div className="rm-campaign-card">
                  {slide.videoSrc ? (
                    <video key={slide.videoSrc} autoPlay muted loop playsInline preload="auto" poster={slide.posterSrc}>
                      <source src={slide.videoSrc} type="video/mp4" />
                    </video>
                  ) : (
                    <img src={slide.posterSrc ?? FALLBACK_POSTER} alt={slide.title} />
                  )}
                  <div className="rm-campaign-overlay">
                    <div className="rm-campaign-ui">
                      <div>
                        <div className="rm-campaign-title">{slide.title}</div>
                        <div className="rm-campaign-desc">{slide.description}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          <div className="rm-campaign-dots" aria-label="Carousel dots">
            {slides.map((_, index) => (
              <div
                key={index}
                className={`rm-campaign-dot ${
                  carouselSlides[centerIndex]?.id === slides[index]?.id ? 'active' : ''
                }`}
                role="button"
                tabIndex={0}
                aria-label={`Go to slide ${index + 1}`}
                onClick={() => { sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: `Carousel Dot ${index + 1}`, buttonLocation: 'RWBYCarousel' }); update(index); markInteraction(); }}
                onKeyDown={(e) => handleKeyActivate(e, () => { update(index); markInteraction(); })}
              />
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderSingleCard = () => {
    if (slideCount !== 1) return null;
    const slide = slides[0];
    return (
      <div
        className="rm-campaign-card-border"
        style={{ width: '320px', height: '200px', cursor: 'pointer' }}
        role="button"
        tabIndex={0}
        onClick={() => openInNewTab(slide.demoUrl)}
        onKeyDown={(e) => handleKeyActivate(e, () => openInNewTab(slide.demoUrl))}
      >
        <div className="rm-campaign-card" style={{ height: '100%' }}>
          {slide.videoSrc ? (
            <video key={slide.videoSrc} autoPlay muted loop playsInline preload="auto" poster={slide.posterSrc}>
              <source src={slide.videoSrc} type="video/mp4" />
            </video>
          ) : (
            <img src={slide.posterSrc ?? FALLBACK_POSTER} alt={slide.title} />
          )}
          <div className="rm-campaign-overlay">
            <div className="rm-campaign-ui">
              <div>
                <div className="rm-campaign-title">{slide.title}</div>
                <div className="rm-campaign-desc">{slide.description}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const sectionHeight = slideCount === 0 ? 160 : SECTION_HEIGHT;

  return (
    <>
      <style>{CAROUSEL_STYLES}</style>

      {/* Desktop Layout */}
      <motion.section
        ref={sectionRef}
        className="relative mx-auto hidden w-full lg:block"
        style={{
          width: '100%',
          height: `${sectionHeight}px`,
          backgroundColor: 'transparent',
          overflow: slideCount <= 1 ? 'visible' : 'hidden',
          position: 'relative',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <motion.div
          style={{ position: 'relative', width: '100%', height: '100%' }}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          {slideCount > 0 && (
            <>
              {/* Decorative Image - Right */}
              <img
                src={DECORATIVE_IMG}
                alt=""
                style={{
                  position: 'absolute',
                  left: '652.5px',
                  top: '10px',
                  width: '606px',
                  height: '350px',
                  transform: 'rotate(-270deg) scaleX(-1)',
                  opacity: 0.43,
                  zIndex: 1,
                  pointerEvents: 'none',
                }}
              />

              {/* Decorative Image - Left */}
              <img
                src={DECORATIVE_IMG}
                alt=""
                style={{
                  position: 'absolute',
                  left: '-237.88px',
                  top: '48px',
                  width: '590px',
                  height: '338px',
                  transform: 'rotate(89deg)',
                  opacity: 0.27,
                  zIndex: 1,
                  pointerEvents: 'none',
                }}
              />
            </>
          )}

          {/* Header */}
          <div
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              top: '20px',
              width: '612px',
              height: '96px',
              zIndex: 5,
            }}
          >
            <h1 className="text-style-carousel-header" style={{ margin: 0 }}>
              PLAY ALL GAMES{' '}
              <span style={{ color: '#FBBF24' }}>INSTANTLY.</span>
              {' '}ANY PLACE, ANY TIME
            </h1>
          </div>

          {/* Subtitle */}
          {/* IM SORRY CHRIS - Here is what I removed from the text below after 'Try ' - <strong style={{ color: '#FBBF24', fontWeight: 700, fontVariationSettings: "'wght' 700" }}>RWBY: Grimm Eclipse</strong>*/}
          <div
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              top: '125px',
              width: '420px',
              zIndex: 5,
              textAlign: 'center',
            }}
          >
            <p className="text-style-paragraph-white" style={{ margin: 0 }}>
              Try now! No download, login, SDK, or install required.
            </p>
          </div>

          {/* Loading indicator */}
          {campaignsLoading && (
            <p
              style={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                top: '155px',
                zIndex: 5,
                fontFamily: 'Inter, sans-serif',
                fontWeight: 900,
                fontStyle: 'italic',
                fontSize: '18px',
                letterSpacing: '-0.5px',
                color: 'rgba(255, 255, 255, 0.6)',
                margin: 0,
                textAlign: 'center',
                fontVariationSettings: "'wght' 900",
              }}
            >
              Loading Game Demos...
            </p>
          )}

          {/* Single Card (1 campaign) */}
          {slideCount === 1 && (
            <div
              style={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                top: '155px',
                zIndex: 10,
              }}
            >
              {renderSingleCard()}
            </div>
          )}

          {/* Carousel (2+ campaigns) */}
          {slideCount >= 2 && (
            <div
              style={{
                position: 'absolute',
                left: '175px',
                top: '120px',
                width: '674px',
                height: '264px',
                zIndex: 10,
              }}
            >
              {renderCarousel()}
            </div>
          )}

          {/* Play Now + Read More Buttons */}
          {slideCount > 0 && (
            <div
              style={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                top: '370px',
                zIndex: 10,
                display: 'flex',
                gap: '12px',
                alignItems: 'center',
              }}
            >
              <button
                type="button"
                className="aida-button aida-button-lg"
                onClick={handlePlayNow}
                style={{ width: '116px' }}
              >
                <span className="text">Play Now!</span>
              </button>
              <a
                href="/news/rwby-grimm-eclipse-launch"
                className="aida-button aida-button-lg aida-button-outline-fill"
                style={{ width: '116px', textDecoration: 'none', textAlign: 'center' }}
              >
                <span className="text">Read More</span>
              </a>
            </div>
          )}
        </motion.div>
      </motion.section>

      {/* Mobile Layout */}
      <motion.section
        ref={mobileRef}
        className="relative flex w-full flex-col items-center lg:hidden"
        style={{
          width: '100%',
          backgroundColor: 'transparent',
          overflow: 'hidden',
          padding: '40px 0 40px',
        }}
        initial={{ opacity: 0 }}
        animate={isMobileInView ? { opacity: 1, y: 0 } : { opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <div style={{ width: '340px', textAlign: 'center' }}>
          <p
            style={{
              lineHeight: '44px',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 900,
              fontSize: '34px',
              letterSpacing: '-0.5px',
              fontStyle: 'italic',
              color: WHITE,
              margin: 0,
              fontVariationSettings: "'wght' 900",
            }}
          >
            PLAY ALL GAMES{' '}
            <span style={{ color: '#FBBF24' }}>INSTANTLY.</span>
          </p>
        </div>

        {/* Subtitle */}
        <p
          style={{
            lineHeight: '22px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: '16px',
            color: WHITE,
            margin: 0,
            marginTop: '12px',
            textAlign: 'center',
            width: '280px',
          }}
        >
          No download, login, SDK, or install required.
        </p>

        {/* Loading indicator */}
        {campaignsLoading && (
          <p
            style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 900,
              fontStyle: 'italic',
              fontSize: '16px',
              letterSpacing: '-0.5px',
              color: 'rgba(255, 255, 255, 0.6)',
              margin: 0,
              marginTop: '24px',
              textAlign: 'center',
              fontVariationSettings: "'wght' 900",
            }}
          >
            Loading Game Demos...
          </p>
        )}

        {/* Scaled carousel cards area + Play Now (only when campaigns exist) */}
        {hasSlides && (
          <>
            <div
              style={{
                position: 'relative',
                width: '100%',
                height: '188px',
                marginTop: '16px',
                overflow: 'visible',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%) scale(0.75)',
                  transformOrigin: 'top center',
                  width: '674px',
                  height: '264px',
                }}
              >
                {slideCount === 1 && (
                  <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
                    {renderSingleCard()}
                  </div>
                )}
                {slideCount >= 2 && renderCarousel()}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '8px' }}>
              <button
                type="button"
                className="aida-button"
                onClick={handlePlayNow}
                style={{
                  width: '130px',
                  height: '35px',
                  borderRadius: '44px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '35px',
                  cursor: 'pointer',
                }}
              >
                <span className="text" style={{ height: '35px', lineHeight: '35px', fontSize: '14px' }}>Play Now!</span>
              </button>
              <a
                href="/news/rwby-grimm-eclipse-launch"
                className="aida-button aida-button-outline-fill"
                style={{
                  width: '130px',
                  height: '35px',
                  borderRadius: '44px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '35px',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  textAlign: 'center',
                  display: 'inline-block',
                }}
              >
                <span className="text" style={{ height: '35px', lineHeight: '35px', fontSize: '14px' }}>Read More</span>
              </a>
            </div>
          </>
        )}
      </motion.section>

    </>
  );
};
